import React from "react";
import { Link } from "react-router-dom";
import { BedDoubleIcon, BathIcon, CarIcon, ArrowRightIcon } from "lucide-react";
import { buttonClass } from "./Button";
import type { Design } from "../../data/designs";
type DesignCardProps = {
  design: Design;
  headingLevel?: "h3" | "h2";
};
export function DesignCard({
  design,
  headingLevel = "h3"
}: DesignCardProps) {
  const Heading = headingLevel;
  return <article className="group flex h-full flex-col overflow-hidden rounded-[28px] border border-line bg-white transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_24px_48px_-28px_rgba(43,39,39,0.45)]" data-id="element-517" data-mp="1ydwhzhb1oy1s">
      <div className="relative aspect-[3/2] overflow-hidden bg-paper-deep" data-id="element-518" data-mp="23d96s7lh8f70">
        <img src={design.image} alt={`${design.name} façade render`} width={698} height={468} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-519" data-mp="23by6ihr8ppkn" />
        <span className="absolute left-5 top-5 rounded-full bg-white/95 px-3 py-1 text-[13px] font-semibold text-charcoal" data-id="element-520" data-mp="2fbdjcu1qz1l7">
          Single-level living
        </span>
        <span className="absolute right-5 top-5 rounded-full bg-charcoal px-3 py-1 text-[13px] font-semibold text-paper" data-id="element-521" data-mp="2p4lyq1q453bk">
          {design.frontage} frontage
        </span>
        <span className="absolute bottom-3 right-3 rounded-full bg-paper/90 px-2.5 py-1 text-[11px] font-medium text-slate" data-id="element-522" data-mp="1wff3bblygtuj">Render</span>
      </div>

      <div className="flex flex-1 flex-col p-6 text-center md:p-8" data-id="element-523" data-mp="21ft0tzpxkleb">
        <Heading className="text-[26px] font-semibold tracking-tight text-charcoal md:text-[30px]" data-id="element-524" data-mp="26c093z5su8zf">{design.name}</Heading>
        <p className="mx-auto mt-2 max-w-sm text-[16px] leading-snug text-slate" data-id="element-525" data-mp="2afpuygv3acgr">{design.tagline}</p>

        <dl className="mt-6 grid grid-cols-3 divide-x divide-line rounded-2xl bg-paper py-4" data-id="element-526" data-mp="3hroysyaftm3x">
          {[{
          label: "Bedrooms",
          value: design.bedrooms,
          icon: BedDoubleIcon
        }, {
          label: "Bathrooms",
          value: design.bathrooms,
          icon: BathIcon
        }, {
          label: "Car spaces",
          value: design.carSpaces,
          icon: CarIcon
        }].map(spec => <div key={spec.label} className="flex flex-col items-center gap-1" data-id="element-527" data-mp="1vof6tzgif086">
              <spec.icon className="h-5 w-5 text-slate" aria-hidden="true" data-id="element-528" data-mp="1qq5dzvi9cbpq" />
              <dt className="order-last text-[13px] text-slate" data-id="element-529" data-mp="130sy6csfd5eg">{spec.label}</dt>
              <dd className="font-heading text-[20px] font-semibold" data-id="element-530" data-mp="12yggm0qkwx1x">{spec.value}</dd>
            </div>)}
        </dl>

        <dl className="mt-4 grid grid-cols-2 gap-4 text-[15px]" data-id="element-531" data-mp="1cr5pqoynpyu3">
          <div data-id="element-532" data-mp="1csp76iw1ufxg">
            <dt className="text-slate" data-id="element-533" data-mp="17zw3zzg8bfnh">Living area</dt>
            <dd className="mt-0.5 font-semibold" data-id="element-534" data-mp="17ycle273osij">{design.livingArea} m²</dd>
          </div>
          <div data-id="element-535" data-mp="k90e57vc2uiv">
            <dt className="text-slate" data-id="element-536" data-mp="k8i9pnyip9zv">Total under roof</dt>
            <dd className="mt-0.5 font-semibold" data-id="element-537" data-mp="fc08uz6b5vst">{design.totalUnderRoof} m²</dd>
          </div>
        </dl>
        <p className="mt-3 text-[14px] text-slate" data-id="element-538" data-mp="2qxivsi0pz1l6">{design.outdoorNote}</p>

        <div className="mt-auto flex flex-col items-center gap-3 pt-7" data-id="element-539" data-mp="2vp21addlmvp1">
          <Link to={`/home-designs/${design.slug}`} className={buttonClass("secondary", "w-full sm:w-auto")} aria-label={`View design: ${design.name}`} data-id="element-540" data-mp="2vr4z3jkztt4b">
            View design
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-541" data-mp="2mx8ei97h8or0" />
          </Link>
          <Link to={`/contact?design=${design.slug}`} className="text-[15px] font-medium text-charcoal underline underline-offset-4 hover:decoration-2" data-id="element-542" data-mp="3anw01xabfcw1">
            Enquire about {design.name.replace("The ", "")}
          </Link>
        </div>
      </div>
    </article>;
}